// src/pages/FinishSetup.jsx
import React, { useEffect, useRef, useState } from 'react';
import { Box, Center, Spinner, Text, useColorModeValue } from '@chakra-ui/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../firebaseConfig';
import { collectionGroup, getDocs, limit, query, where } from 'firebase/firestore';

import { readPendingOrg, deletePendingOrg } from '../services/signup/pendingOrgsService';
import { bootstrapSelfOrgAndRefresh } from '../services/signup/bootstrapService';

// Map pending doc into the callable payload shape the backend expects.
function toServerPayload(p) {
  if (!p) return {};
  return {
    companyName: p.companyName,
    traderType: p.traderType,
    businessType: p.traderType,
    owner: {
      firstName: p.firstName,
      lastName: p.lastName,
      email: p.email,
      mobile: p.mobile,
      phone: p.mobile,
      website: p.website,
    },
    // Also flatten for legacy handlers
    firstName: p.firstName,
    lastName: p.lastName,
    email: p.email,
    mobile: p.mobile,
    website: p.website,
    termsAcceptedAt: p.termsAcceptedAt || new Date().toISOString(),
  };
}

export default function FinishSetup() {
  const navigate = useNavigate();
  const location = useLocation();
  const [msg, setMsg] = useState('Finishing setup…');
  const ran = useRef(false);

  const cardBg = useColorModeValue('white', 'gray.800');
  const pageBg = useColorModeValue('gray.50', 'gray.900');
  const subtle = useColorModeValue('gray.600', 'gray.300');

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const run = async () => {
      try {
        const user = auth.currentUser;

        // Not signed in → go to login
        if (!user) {
          navigate('/login', { replace: true });
          return;
        }

        setMsg('Checking pending setup…');

        // 1) Resume from pendingOrgs if present (idempotent)
        try {
          const snap = await readPendingOrg(user.uid);
          if (snap?.exists()) {
            const pending = snap.data();
            setMsg('Completing your organisation setup…');
            const payload = toServerPayload(pending);
            const result = await bootstrapSelfOrgAndRefresh(payload);
            if (result?.ok) {
              await deletePendingOrg(user.uid).catch(() => {});
              // 👇 NEW landing: Sales People for first-time completion
              navigate('/settings/sales-people', { replace: true });
              return;
            } else {
              setMsg(result?.message || 'Setup could not be completed. Redirecting…');
              setTimeout(() => {
                navigate('/signup', {
                  replace: true,
                  state: { note: 'Let’s finish setting up your account.' },
                });
              }, 600);
              return;
            }
          }
        } catch {
          // ignore pending read errors; continue
        }

        // 2) No pending doc — try claims refresh
        setMsg('Refreshing your access…');
        await user.getIdToken(true);
        const res = await user.getIdTokenResult();
        if (res?.claims?.orgId) {
          navigate('/leads', { replace: true });
          return;
        }

        // 3) Check membership via collection group on members (expects member docs store `uid`)
        setMsg('Checking membership…');
        try {
          const q = query(collectionGroup(db, 'members'), where('uid', '==', user.uid), limit(1));
          const cg = await getDocs(q);
          if (!cg.empty) {
            await user.getIdToken(true);
            navigate('/leads', { replace: true });
            return;
          }
        } catch {
          // ignore query errors; continue
        }

        // 4) Nothing to resume and no membership found — send to signup
        setMsg('No setup found. Redirecting to signup…');
        navigate('/signup', {
          replace: true,
          state: { note: 'Please create your organisation to continue.' },
        });
      } catch {
        // Any unexpected error → fail gracefully to signup
        setMsg('We hit a snag. Redirecting to signup…');
        navigate('/signup', {
          replace: true,
          state: { note: 'Please create your organisation to continue.' },
        });
      }
    };

    run();
  }, [navigate, location]);

  return (
    <Center minH="100vh" bg={pageBg} px={6}>
      <Box
        w="100%"
        maxW="520px"
        bg={cardBg}
        borderWidth="1px"
        borderRadius="lg"
        boxShadow="md"
        p={8}
        textAlign="center"
      >
        <Spinner thickness="4px" speed="0.65s" size="lg" />
        <Text mt={4} fontSize="md" color={subtle}>
          {msg}
        </Text>
        <Text mt={2} fontSize="sm" color={subtle}>
          This will only take a moment.
        </Text>
      </Box>
    </Center>
  );
}
